import React, { useEffect, useState } from 'react';
import { Shield, Calculator, Globe } from 'lucide-react';

const HomeSection = ({ onNavigate }) => {
  const [counters, setCounters] = useState({
    years: 0,
    partners: 0,
    countries: 0,
    sectors: 0
  });

  useEffect(() => {
    const targets = { years: 20, partners: 3, countries: 2, sectors: 15 };
    const duration = 2000;
    const steps = 60;
    const increment = duration / steps;

    const intervals = Object.keys(targets).map((key) => {
      let current = 0;
      return setInterval(() => {
        current += targets[key] / steps;
        if (current >= targets[key]) {
          setCounters((prev) => ({ ...prev, [key]: targets[key] }));
          clearInterval(intervals.find((i) => i));
        } else {
          setCounters((prev) => ({ ...prev, [key]: Math.floor(current) }));
        }
      }, increment);
    });

    return () => intervals.forEach(clearInterval);
  }, []);

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Hero Content */}
        <div className="text-center mb-16 animate-fadeIn">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-[#C9A84C] mb-6">
            Integrity. Expertise. Excellence.
          </h1>
          <p className="text-lg sm:text-xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed font-dmsans text-justify ">
            Built on Integrity. Driven by Expertise. Committed to Excellence. At AGDG & Associates, we believe that great financial advice goes beyond numbers — it builds trust, enables growth, and creates lasting value. With a team of seasoned Chartered Accountants and a legacy of professional excellence, we partner with businesses and individuals to navigate complex financial landscapes with confidence and clarity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => onNavigate('services')}
              className="px-8 py-3 bg-[#C9A84C] text-[#0D1B2A] font-dmsans font-semibold rounded hover:bg-[#b8973d] transition-all duration-300 transform hover:scale-105">

              Explore Our Services
            </button>
            <button
              onClick={() => onNavigate('team')}
              className="px-8 py-3 border-2 border-[#C9A84C] text-[#C9A84C] font-dmsans font-semibold rounded hover:bg-[#C9A84C] hover:text-[#0D1B2A] transition-all duration-300 transform hover:scale-105">

              Meet Our Team
            </button>
          </div>
        </div>

        {/* Stats Counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div className="text-center p-6 bg-[#1a2d42] bg-opacity-50 rounded-lg backdrop-blur-sm border border-[#C9A84C] border-opacity-20">
            <div className="text-4xl font-playfair font-bold text-[#C9A84C] mb-2">
              {counters.years}+
            </div>
            <div className="text-gray-300 font-dmsans">Years Experience</div>
          </div>
          <div className="text-center p-6 bg-[#1a2d42] bg-opacity-50 rounded-lg backdrop-blur-sm border border-[#C9A84C] border-opacity-20">
            <div className="text-4xl font-playfair font-bold text-[#C9A84C] mb-2">
              {counters.partners}
            </div>
            <div className="text-gray-300 font-dmsans">Partners</div>
          </div>
          <div className="text-center p-6 bg-[#1a2d42] bg-opacity-50 rounded-lg backdrop-blur-sm border border-[#C9A84C] border-opacity-20">
            <div className="text-4xl font-playfair font-bold text-[#C9A84C] mb-2">
              {counters.countries}
            </div>
            <div className="text-gray-300 font-dmsans">Countries</div>
          </div>
          <div className="text-center p-6 bg-[#1a2d42] bg-opacity-50 rounded-lg backdrop-blur-sm border border-[#C9A84C] border-opacity-20">
            <div className="text-4xl font-playfair font-bold text-[#C9A84C] mb-2">
              {counters.sectors}+
            </div>
            <div className="text-gray-300 font-dmsans">Multiple Sectors</div>
          </div>
        </div>

        {/* Highlight Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-[#FAF8F3] p-8 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-b-4 border-[#C9A84C]">
            <Shield className="text-[#C9A84C] mb-4" size={48} />
            <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-3">
              Audit & Assurance
            </h3>
            <p className="text-gray-700 font-dmsans">
              Independent, objective assurance across statutory, tax, internal, and sector-specific audits 
              under Indian regulations and international standards.
            </p>
          </div>
          <div className="bg-[#FAF8F3] p-8 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-b-4 border-[#C9A84C]">
            <Calculator className="text-[#C9A84C] mb-4" size={48} />
            <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-3">
              Taxation & GST
            </h3>
            <p className="text-gray-700 font-dmsans">
              Comprehensive direct and indirect tax services including compliance, planning, filing, 
              and representation for individuals, businesses, and NRIs.
            </p>
          </div>
          <div className="bg-[#FAF8F3] p-8 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-b-4 border-[#C9A84C]">
            <Globe className="text-[#C9A84C] mb-4" size={48} />
            <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-3">
              International Advisory
            </h3>
            <p className="text-gray-700 font-dmsans">
              Cross-border financial services, IFRS compliance, and strategic advisory for clients 
              with India-UAE linkages through our Dubai branch.
            </p>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-[#FAF8F3] p-8 rounded-lg">
  <h2 className="text-3xl font-playfair font-bold text-[#0D1B2A] mb-6 text-center">
    About the Firm
  </h2>

  <div className="text-gray-700 font-dmsans text-justify max-w-4xl mx-auto leading-relaxed space-y-6">

    <p>
      AGDG & Associates established in 2004, has grown into a well-respected and trusted name in the field of Chartered Accountancy. Over the years, the firm has achieved significant professional standing and expanded its footprint to include an international presence in Dubai, UAE.
    </p>

    <p>
      The firm is driven by a highly motivated, disciplined, and well-trained team of Chartered Accountants, Information System Auditors, and consultants. With a focus on delivering forward-looking and practical solutions, the firm offers a comprehensive range of services spanning auditing, taxation, project financing, and advisory.
    </p>

    <p>
      At AGDG & Associates, value creation is anchored in the right blend of people, expertise, and robust processes. The team comprises hands-on professionals with extensive audit experience and deep domain knowledge across sectors such as banking, FMCG, real estate, retail, manufacturing, and information technology. The firm also possesses strong practical exposure to international financial reporting frameworks, including IFRS and IAS. The firm’s approach is rooted in risk-based and comprehensive methodologies, executed in accordance with the highest professional standards. This ensures accuracy, reliability, and consistency across all engagements.
    </p>

    <p>
      With a proven track record of seamless service delivery and a “no surprises” philosophy, the firm focuses on providing insightful and pragmatic solutions aligned with clients’ strategic objectives.
    </p>

    <p>
      With offices strategically located in Jaipur, India and Dubai, UAE, AGDG & Associates serves a diverse clientele ranging from emerging enterprises to well-established organizations, both domestically and internationally. The firm remains committed to upholding the highest standards of credibility, integrity, and professionalism, while consistently delivering informed advice, dependable support, and long-term value to its clients.
    </p>

  </div>

  <p className="text-sm text-gray-600 font-dmsans text-center italic mt-6">
    This website is not an advertisement or solicitation. Information published here is only 
    for general awareness and does not constitute professional advice.
  </p>
</div>
      </div>
    </section>);};
export default HomeSection;